import type { DbState } from './types'
import { REQUIRED_MSG, dropStyle, statusOptions, dbToolOptions, TENANT_SCOPE_OPTIONS } from './constants'
import { FieldCell, TwoColRow } from './styles'
import WizrInput from '../../WizrInput'
import WizrDropDown from '../../WizrDropDown'

interface DbFormFieldsProps {
  dbState: DbState
  setDbField: (key: keyof DbState, value: string) => void
  dbErrors: Partial<Record<string, boolean>>
  clearDbErrorKey: (key: string) => void
  dbTenantScopeSelected: boolean
  setDbTenantScopeSelected: (v: boolean) => void
  productOptions: { label: string; value: string }[]
  productListPending: boolean
}

const inputStyle = { width: '100%', marginTop: '0' } as const
const inputBg = '#F2F4F9' as const

const DbFormFields = ({
  dbState,
  setDbField,
  dbErrors,
  clearDbErrorKey,
  setDbTenantScopeSelected,
  productOptions,
  productListPending,
}: DbFormFieldsProps): JSX.Element => (
  <>
    <TwoColRow
      left={
        <FieldCell label='Display Name' fieldKey='db-displayName'>
          <WizrInput
            style={inputStyle}
            border='1px solid #000'
            background={inputBg}
            placeholder='Enter Name'
            value={dbState.displayName}
            onChange={(v: string) => {
              setDbField('displayName', v)
              if (v?.trim()) clearDbErrorKey('db-displayName')
            }}
            showError={!!dbErrors['db-displayName']}
            errorMessage={dbErrors['db-displayName'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='Database Tool' fieldKey='db-toolKey'>
          <WizrDropDown
            placeHolder='Select Tool'
            options={dbToolOptions}
            value={dbState.toolKey || undefined}
            onChange={(v: string) => {
              setDbField('toolKey', v)
              if (v) clearDbErrorKey('db-toolKey')
            }}
            style={dropStyle}
            showError={!!dbErrors['db-toolKey']}
            errorMessage={dbErrors['db-toolKey'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='Host / Connection' fieldKey='db-connectionHost'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter Host or Connection String'
            value={dbState.connectionHost}
            onChange={(v: string) => {
              setDbField('connectionHost', v)
              if (v?.trim()) clearDbErrorKey('db-connectionHost')
            }}
            showError={!!dbErrors['db-connectionHost']}
            errorMessage={dbErrors['db-connectionHost'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='Port' fieldKey='db-port'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter Port'
            value={dbState.port}
            onChange={(v: string) => {
              setDbField('port', v)
              if (v?.trim()) clearDbErrorKey('db-port')
            }}
            showError={!!dbErrors['db-port']}
            errorMessage={dbErrors['db-port'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='Database Name' fieldKey='db-databaseName'>
          <WizrInput
            style={inputStyle}
            background={inputBg}
            placeholder='Enter Database Name'
            value={dbState.databaseName}
            onChange={(v: string) => {
              setDbField('databaseName', v)
              if (v?.trim()) clearDbErrorKey('db-databaseName')
            }}
            showError={!!dbErrors['db-databaseName']}
            errorMessage={dbErrors['db-databaseName'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='Status' fieldKey='db-status'>
          <WizrDropDown
            placeHolder='Select Status'
            options={statusOptions}
            value={dbState.status || undefined}
            onChange={(v: string) => {
              setDbField('status', v)
              if (v) clearDbErrorKey('db-status')
            }}
            style={dropStyle}
            showError={!!dbErrors['db-status']}
            errorMessage={dbErrors['db-status'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <TwoColRow
      left={
        <FieldCell label='Tenant' fieldKey='db-tenantScope'>
          <WizrDropDown
            placeHolder='Select Tenant'
            options={TENANT_SCOPE_OPTIONS}
            value={dbState.tenantId || undefined}
            onChange={(v: string) => {
              setDbField('tenantId', v)
              setDbTenantScopeSelected(true)
              clearDbErrorKey('db-tenantScope')
            }}
            style={dropStyle}
            showError={!!dbErrors['db-tenantScope']}
            errorMessage={dbErrors['db-tenantScope'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
      right={
        <FieldCell label='Select Product' fieldKey='db-productId'>
          <WizrDropDown
            placeHolder={productListPending ? 'Loading products...' : 'Select Product'}
            loading={productListPending}
            options={productOptions}
            value={dbState.productId || undefined}
            onChange={(v: string) => {
              setDbField('productId', v)
              if (v) clearDbErrorKey('db-productId')
            }}
            style={dropStyle}
            showError={!!dbErrors['db-productId']}
            errorMessage={dbErrors['db-productId'] ? REQUIRED_MSG : undefined}
          />
        </FieldCell>
      }
    />

    <FieldCell label='Notes' fieldKey='db-notes'>
      <WizrInput
        style={inputStyle}
        background={inputBg}
        placeholder='Enter notes'
        value={dbState.notes}
        onChange={(v: string) => {
          setDbField('notes', v)
          if (v?.trim()) clearDbErrorKey('db-notes')
        }}
        showError={!!dbErrors['db-notes']}
        errorMessage={dbErrors['db-notes'] ? REQUIRED_MSG : undefined}
      />
    </FieldCell>
  </>
)

export default DbFormFields
